import React, {useEffect, useRef, useState} from "react";
import CCInfo from "../models/CCInfo";
import GlobalConst from "../../../../constants/GlobalConst";
import {useDispatch, useSelector} from "react-redux";
import {selectUserAccount} from "../../../../stores/account/account.selectors";
import tradefanApi, {useGetProductsQuery} from "../../../../stores/tradefan/tradefan.api";
import {
    resetPaysafeTransactionStatus,
    setPaysafeTransactionStatus,
    setSessionId
} from "../../../../stores/ecommerce/ecommerce.slice";
import {updateAddressAsync} from "../../../../stores/account/account.thunks";
import {triggerEventAsync} from "../../../../stores/marketing/marketing.thunks";

export const ViewMode = {
    PRODUCT_SELECTION: 0,
    ENTER_BILLING_INFO: 1,
    CONFIRMATION: 2,
    ORDER_PROCESSING: 3,
    ORDER_CONFIRMED: 4,
    ORDER_PROCESSING_ERROR: 5,
    ID_VALIDATION_REQUIRED: 6
};

const requestTimeoutMsg = "The request timed out, please try again or contact support.";

const usePaySafeVm = (props) => {

    const dispatch = useDispatch();
    const prevViewModeRef = useRef();
    const acc = useSelector(selectUserAccount);
    const errors = useSelector(state => state.ecommerce.errors);
    const [viewMode, setViewMode] = useState(ViewMode.PRODUCT_SELECTION);
    const [account, setAccount] = useState(acc);
    const [idVerificationPrompted, setIdVerificationPrompted] = useState(false);
    const {data:products, error:productsError} = useGetProductsQuery();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [paysafeToken, setPaysafeToken] = useState(null);
    const [userAgreed, setUserAgreed] = useState(false);
    const [isBillingFormDataValid, setIsBillingFormDataValid] = useState(false);
    const [billingError, setBillingError] = useState(null);
    const [onBillingContinueCallBack, setOnBillingContinueCallBack] = useState(null);
    const paysafeTransactionStatus = useSelector(state => state.ecommerce.paysafe.transactionStatus);
    const [saveAsHomeAddress, setSaveAsHomeAddress] = useState(false);
    const [ccInfo, setCcInfo] = useState(new CCInfo({
                                            currency: "USD",
                                            ptype: 'Visa',
                                            fname: acc.firstName,
                                            lname: acc.lastName,
                                            zip: acc.address.zipCode,
                                            city: acc.address.city,
                                            address: acc.address.address1,
                                            country: acc.address.countryCode || "US",
                                            state: acc.address.stateCode || "NY",
                                            phone: acc.address.phone1}));
    const [billingAddress, setBillingAddress] = useState(account.address);

    const firedRef = useRef(false);

    const [displayProducts, setDisplayProducts] = useState([]);

    useEffect(()=>{

        const hasProducts = !!products && products.length > 0;

        if(displayProducts.length === 0 && hasProducts) {
            const prods = products.filter(p=>p.name != "Other");
            const other = products.find(p=>p.name === "Other");
            setDisplayProducts([...prods, {...other, price: GlobalConst.MIN_DEPOSIT_FUNDS}]);
            //set default product — prefer entry fee from parlay context, fallback to global default
            const targetAmount = parseFloat(props.parlayContext?.entryFee ?? GlobalConst.DEFAULT_DEPOSIT_AMOUNT);
            const matchedProduct = prods.find(p => parseFloat(p.price) === targetAmount);
            if (matchedProduct) {
                setSelectedProduct(matchedProduct);
            } else if (other) {
                setSelectedProduct({...other, price: targetAmount});
            } else if (prods.length > 0) {
                setSelectedProduct(prods.find(p => p.price === GlobalConst.DEFAULT_DEPOSIT_AMOUNT) || prods[0]);
            }
        }

        if (hasProducts && !firedRef.current) {
            firedRef.current = true;
            dispatch(triggerEventAsync({ eventName: 'addFundsStart', products: products }));
        }

    },[products, dispatch]);

    useEffect(() => {
        dispatch(resetPaysafeTransactionStatus());
        setBillingError(null);
    }, []);

    useEffect(() => {
        const prevViewMode = prevViewModeRef.current;
        if (viewMode === ViewMode.ENTER_BILLING_INFO && prevViewMode !== ViewMode.ENTER_BILLING_INFO) {
            initPaySafe();
        }

        //if( viewMode === ViewMode.ORDER_CONFIRMED)
        //    MacroAction.trackAction(null, "Added Funds", "Add Funds", "Has made a deposit");

    }, [viewMode]);

    useEffect(() => {
        if(paysafeToken){
            onContinueClick();
        }
    }, [paysafeToken]);

    const initPaySafe = () => {
        let paysafe = window.paysafe;
        let apiKey = GlobalConst.PAYSAFE.API_KEY;
        let options = {
            environment: "LIVE",
            fields: {
                cardNumber: {
                    selector: ".cardNumber",
                    placeholder: "Card number",
                    separator: " "
                },
                expiryDate: {
                    selector: ".expiryDate",
                    placeholder: "MM/YY"
                },
                cvv: {
                    selector: ".cvv",
                    placeholder: "CVV",
                    optional: false
                }
            },
            style: {
                input: {
                    "font-family": "robotoregular,Helvetica,Arial,sans-serif",
                    "font-weight": "normal",
                    "font-size": "14px",
                    "color":"white",
                },
                "#card-number.valid": {
                    "color": "#00c97b"
                },
                ":focus": {
                    "color": "white"
                }
            }
        };

        paysafe.fields.setup(apiKey, options, (instance, error) => {
            if( error )
                console.error(error);

            let paysafeTokenize = ()=>{
                let theTimeout = setTimeout(()=>{
                    setBillingError(requestTimeoutMsg);
                }, 30*1000);

                instance.tokenize((instance, error, result) => {

                    clearTimeout(theTimeout);

                    if (error) {
                        console.error(error);
                        setBillingError(error.message);
                    } else {
                        setBillingError(null);
                        setPaysafeToken(result.token);
                    }
                });
            };

            setOnBillingContinueCallBack(() => paysafeTokenize);
        });
    }

    const onContinueClick = () => {
        if(!ccInfo.isValid || !paysafeToken){
            setBillingError(ccInfo.getValidationError() || "Please Review Data Entered.");
            return;
        }

        setBillingError(null);
        setCcInfo(ccInfo.set("paysafeToken", paysafeToken))
        setViewMode(ViewMode.CONFIRMATION);
    }

    const onProductSelected = (p)=>{
        //setViewMode(ViewMode.ENTER_BILLING_INFO);
        setSelectedProduct(p);
    }

    const openVerificationPopUp = () => {
        /*MacroAction.openIdValidationPopUp(() => {
            setViewMode(account.idStatus != "Verified" ? ViewMode.ID_VALIDATION_REQUIRED : ViewMode.PRODUCT_SELECTION);
        })*/
    }

    const handleProductionSelectionSubmitClick = () => {
        setViewMode(ViewMode.ENTER_BILLING_INFO);

        //if(selectedProduct?.name === "Other")
        //    setProducts(products.update(products.findIndex(p => p.name === "Other"), p => selectedProduct));
    }

    const onReturnToProdSelectionClick = () =>{
           setViewMode(ViewMode.PRODUCT_SELECTION);
    }

    const onCloseBtnClick = () => {
        setViewMode(ViewMode.PRODUCT_SELECTION);
        setUserAgreed(false);

        props.onClose();
        if(props.onCompleted)
            props.onCompleted();
    };
    let tTimeOut;
    const onProceedPurchaseClick = async () => {
        dispatch(setPaysafeTransactionStatus({transactionStatus:"Processing"}));
        tTimeOut = setTimeout(()=>{
            setBillingError(requestTimeoutMsg);
        }, 30*1000);
        setViewMode(ViewMode.ORDER_PROCESSING);

        try {
            const addFundsRes = await dispatch(tradefanApi.endpoints.addFunds.initiate({
                product: selectedProduct,
                ccInfo: ccInfo,
                hostname: window.location.hostname === "localhost"
                    ? "pickx.tradefan" + GlobalConst.SITE_TLD
                    : window.location.hostname,
            }));
            dispatch(setSessionId(addFundsRes.data.SessionId));
        }catch(err){
            console.error(err);
            setBillingError(err.message);
            setViewMode(ViewMode.ORDER_PROCESSING_ERROR);
        }
    }

    const onUserAgreed = (val) => {
        setUserAgreed(val);
    }
    const handleOrderConfirmedOkClick = ()=>{
        props.onClose();
        if(props.onCompleted)
            props.onCompleted();
    };

    const onBillingContinueClick = () => {
        if(saveAsHomeAddress){
            setCcInfo(ccInfo
                .set("address", billingAddress.address1)
                .set("city", billingAddress.city)
                .set("zip", billingAddress.zipCode)
                .set("country", billingAddress.country)
                .set("state", billingAddress.state)
                .set("countryCode", billingAddress.countryCode)
                .set("stateCode", billingAddress.stateCode)
                .set("phone", billingAddress.phone1)
                .set("isCustom", billingAddress.isCustom));
            dispatch(updateAddressAsync(billingAddress))
        }

        if(onBillingContinueCallBack)
            onBillingContinueCallBack();
    }



    useEffect(() => {
        if (paysafeTransactionStatus === "Approved") {
            setViewMode(ViewMode.ORDER_CONFIRMED);
        } else if (paysafeTransactionStatus === "Failed") {
            setViewMode(ViewMode.ORDER_PROCESSING_ERROR);
        }
    }, [paysafeTransactionStatus]);

    return {
        account,
        billingAddress, setBillingAddress,
        products,
        displayProducts,
        viewMode,setViewMode,
        selectedProduct,
        paysafeToken,
        userAgreed,
        isBillingFormDataValid,
        errors,
        billingError,

        ccInfo,
        setSelectedProduct,
        setCcInfo,
        idVerificationPrompted,
        setIdVerificationPrompted,
        setIsBillingFormDataValid,
        saveAsHomeAddress, setSaveAsHomeAddress,

        onBillingContinueClick,
        handleProductionSelectionSubmitClick,
        handleOrderConfirmedOkClick,
        onProductSelected,
        onReturnToProdSelectionClick,
        onCloseBtnClick,
        onProceedPurchaseClick,
        onUserAgreed,
        openVerificationPopUp,
        onContinueClick

    }

}

export default usePaySafeVm;
